import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Loader2, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FacilityIcon } from "@/components/icons";
import type { AuthSession } from "../../auth/keycloak.js";
import { useFacilities } from "../facilities/useFacilities.js";
import { useManualBody } from "../facilities/useManualMutations.js";
import { buildForest, excludeArchived, type FacilityTreeNode } from "../facilities/facility-tree.js";
import { FacilityRailList } from "../facilities/drilldown/FacilityRailList.js";
import { FacilityBreadcrumb } from "../facilities/drilldown/FacilityBreadcrumb.js";
import { ManualsViewShell } from "../facilities/drilldown/ManualsViewShell.js";
import { ManualViewer } from "../facilities/manual-viewer/ManualViewer.js";
import { useFacilityNavParams } from "../facilities/drilldown/useFacilityNavParams.js";
import { consumeKioskParam, forgetDeviceProfileId, readDeviceProfileId, readKioskParam, rememberDeviceProfileId } from "./kiosk-device.js";
import { isInKioskScope, kioskAncestors, kioskTopFacilities, resolveKioskLanding } from "./kiosk-landing.js";
import { useMyKiosk } from "./useKiosk.js";

/**
 * The shop-floor kiosk landing (ADR-036 Phase 3) — the legacy's "Wähle dein Department" chooser and the facility
 * selection scoped to the profile, rebuilt as a screen of its own.
 *
 * **Locked down by construction, not by CSS.** The legacy hid the dashboard button, the favourites shortcuts and the
 * editing affordances with `display: none` on a `.genericUser` class, so every one of them was still in the DOM and a
 * devtools toggle away. Here they are simply never rendered: the kiosk passes `canManage`/`canConfirm` false, gets no
 * kebab, no star, no create, no global search — and the server re-checks everything anyway.
 *
 * **It can always recover itself** (§3), because nobody can walk to a kiosk: a deleted profile re-points the device
 * silently when the realm has exactly one left, several offer a one-tap picker, and the t("viewer.leaveKiosk") escape
 * hatch is always on screen so even a mis-configured account gets out in-product.
 */
export function KioskView({ session, onLeave }: { session: AuthSession; onLeave: () => void }) {
  const { t } = useTranslation();
  const nav = useFacilityNavParams();
  // `?arc_kiosk=<id>` is how an admin points a device the first time; after that the device remembers on its own.
  const [profileId, setProfileId] = useState<number | null>(() => readKioskParam(window.location.search) ?? readDeviceProfileId());
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const kiosk = useMyKiosk(session, profileId);
  const facilities = useFacilities(session);
  // The reader's OWN body read, by the same query key `ManualViewer` uses — React Query dedupes it, so this costs no
  // extra request and simply lets the kiosk learn which facility a `?arc_manual` deep link actually belongs to.
  const readerBody = useManualBody(session, nav.openManualId);

  const data = kiosk.data;
  const resolvedId = data?.profile?.id ?? null;
  const missing = data?.requestedProfileMissing ?? false;
  const soleAvailableId = data && data.available.length === 1 ? data.available[0]!.id : null;

  // Persist the id the moment it resolves — a device that started in a one-profile realm must not be orphaned when a
  // SECOND profile appears later (the implicit resolve would stop working). This is the whole reason the id is
  // remembered rather than re-derived on every visit.
  useEffect(() => {
    if (resolvedId === null) return;
    rememberDeviceProfileId(resolvedId);
    // The link has done its one job; leaving it in the URL would outrank the stored id forever (diff panel, Medium).
    consumeKioskParam();
    setProfileId((cur) => (cur === resolvedId ? cur : resolvedId));
  }, [resolvedId]);

  // The remembered profile is gone: drop it, and if the realm has exactly ONE profile left, re-point at it without a
  // single tap — the device heals itself while the shop floor is asleep. With several, the picker below says why.
  useEffect(() => {
    if (!missing) return;
    forgetDeviceProfileId();
    consumeKioskParam(); // the id in the link is the dead one — it must not come back on the next reload
    if (soleAvailableId !== null) setProfileId(soleAvailableId);
  }, [missing, soleAvailableId]);

  // A CHANGE of profile ⇒ a different set of departments; never carry a drill position across. The FIRST resolve is
  // not a change: resetting there would wipe a `?arc_facility` / `?arc_manual` deep link on every load, so an
  // operator who reloads mid-manual would lose their place — on the one device where that is hardest to recover from.
  const lastProfile = useRef<number | null>(null);
  useEffect(() => {
    const previous = lastProfile.current;
    lastProfile.current = resolvedId;
    if (previous === null || previous === resolvedId) return;
    setDepartmentId(null);
    nav.drillTo(null);
    // Deliberately keyed on the profile alone: `nav` is rebuilt every render, so listing it would reset on each one.
  }, [resolvedId]);

  const landing = useMemo(() => (data ? resolveKioskLanding(data, departmentId) : null), [data, departmentId]);
  // The chosen department's own top level. Hoisted above every early return so the reader gate below is a hook-safe
  // derivation rather than something only the department branch can see.
  const tops = useMemo(() => (landing?.kind === "department" ? kioskTopFacilities(landing.department.facilities) : []), [landing]);
  const topIds = useMemo(() => new Set(tops.map((t) => t.id)), [tops]);

  const flat = useMemo(() => excludeArchived(facilities.data ?? []), [facilities.data]);
  const byId = useMemo(() => new Map(flat.map((f) => [f.id, f] as const)), [flat]);
  const treeById = useMemo(() => {
    const index = new Map<number, FacilityTreeNode>();
    const walk = (nodes: readonly FacilityTreeNode[]) => {
      for (const n of nodes) {
        index.set(n.id, n);
        walk(n.children);
      }
    };
    walk(buildForest(flat));
    return index;
  }, [flat]);

  // The reader is scope-gated like the manuals grid (diff panel, Medium): a stale or hand-typed `?arc_manual` from
  // another department must not put another line's work instruction on this screen. `null` while the body loads means
  // "not known yet", so a legitimate reload mid-manual is never interrupted.
  const readerFacilityId = readerBody.data?.facilityPath.at(-1)?.id ?? null;
  const readerNode = readerFacilityId === null ? null : (byId.get(readerFacilityId) ?? null);
  const readerOutOfScope = nav.openManualId !== null && readerNode !== null && !isInKioskScope(readerNode, tops);
  useEffect(() => {
    if (readerOutOfScope) nav.dropStale("arc_manual"); // keep the URL honest about what is on screen
    // Keyed on the verdict alone: `nav` is rebuilt every render.
  }, [readerOutOfScope]);

  const leave = (
    <Button variant="ghost" size="sm" onPress={onLeave} className="text-muted-foreground shrink-0">
      <LogOut aria-hidden="true" className="size-4" /> {t("viewer.leaveKiosk")}
    </Button>
  );
  const frame = (title: string, subtitle: string | null, body: ReactNode) => (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-border border-b py-4">
        <div className="page-column flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold">{title}</h1>
            {subtitle !== null ? <p className="text-muted-foreground truncate text-sm">{subtitle}</p> : null}
          </div>
          {leave}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="page-column py-6">{body}</div>
      </div>
    </div>
  );

  if (kiosk.isPending) {
    return frame(
      "Kiosk",
      null,
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("viewer.loadingDeviceKiosk")}
      </p>,
    );
  }
  // A failed read is never a dead screen: it always offers its own way forward.
  if (kiosk.isError || landing === null) {
    return frame(
      "Kiosk",
      null,
      <div className="flex flex-col items-start gap-3">
        <p role="alert" className="text-destructive text-sm">
          {t("viewer.couldntReachTheServerTheDevice")}
        </p>
        <Button variant="outline" size="sm" onPress={() => void kiosk.refetch()} isDisabled={kiosk.isFetching}>
          {t("viewer.tryAgain")}
        </Button>
      </div>,
    );
  }

  const pick = (id: number) => {
    rememberDeviceProfileId(id);
    setProfileId(id);
  };

  if (landing.kind === "unconfigured") {
    return frame(
      "Kiosk",
      null,
      <p className="text-muted-foreground max-w-prose text-sm">
        {t("viewer.noKioskProfileYet")}
      </p>,
    );
  }

  if (landing.kind === "pick") {
    return frame(
      t("viewer.chooseThisDevicesKiosk"),
      landing.missing ? null : t("viewer.thisDeviceHasNotBeenPointed"),
      <div className="flex flex-col gap-4">
        {landing.missing ? (
          <p role="status" className="text-muted-foreground max-w-prose text-sm">
            {t("viewer.theProfileThisDeviceWasUsing")}
          </p>
        ) : null}
        <ul aria-label={t("viewer.kioskProfiles")} className="m-0 flex list-none flex-col gap-2 p-0">
          {landing.available.map((p) => (
            <li key={p.id}>
              <Button variant="outline" onPress={() => pick(p.id)} className="w-full justify-start">
                {p.name}
              </Button>
            </li>
          ))}
        </ul>
      </div>,
    );
  }

  if (landing.kind === "empty") {
    return frame(
      landing.profile.name,
      null,
      <div className="flex flex-col items-start gap-3">
        <p className="text-muted-foreground max-w-prose text-sm">
          {t("viewer.thisKioskProfileHasNoFacility")}
        </p>
        {data && data.available.length > 1 ? (
          <Button variant="outline" size="sm" onPress={() => setProfileId(null)}>
            {t("viewer.chooseADifferentKiosk")}
          </Button>
        ) : null}
      </div>,
    );
  }

  if (landing.kind === "departments") {
    // The legacy's "Wähle dein Department" — two hard-coded blocks there, any number of named ones here (D2).
    return frame(
      t("viewer.chooseYourDepartment"),
      landing.profile.name,
      <ul aria-label={t("viewer.departments")} className="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2">
        {landing.profile.departments.map((d) => (
          <li key={d.id}>
            <button
              type="button"
              onClick={() => setDepartmentId(d.id)}
              className="border-border hover:bg-muted focus-visible:ring-ring/50 flex w-full cursor-pointer flex-col items-start gap-1 rounded-lg border bg-transparent p-5 text-left outline-none focus-visible:ring-2"
            >
              <span className="text-lg font-semibold">{d.name}</span>
              <span className="text-muted-foreground text-sm">
                {t("viewer.countFacilities", { count: d.facilities.length })}
              </span>
            </button>
          </li>
        ))}
      </ul>,
    );
  }

  // ── a department is in play ─────────────────────────────────────────────────────────────────────────────────────
  const department = landing.department;
  const backToDepartments = landing.soleDepartment
    ? null
    : () => {
        setDepartmentId(null);
        nav.drillTo(null);
      };

  // The full-screen reader wins over everything, exactly as it does in the normal navigator — a kiosk reading a
  // manual is the whole point of the device (scope-gated above).
  if (nav.openManualId !== null && !readerOutOfScope) {
    return (
      <ManualViewer
        key={nav.openManualId}
        session={session}
        manualId={nav.openManualId}
        onClose={() => nav.dropStale("arc_manual")}
        onExitToFacility={(id) => nav.openManuals(id, true)}
      />
    );
  }

  const manualsNode = nav.manualsId === null ? null : (treeById.get(nav.manualsId) ?? null);
  if (manualsNode && isInKioskScope(manualsNode, tops)) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {/* The escape hatch is on EVERY kiosk screen (D3 guard 2), so it rides above the manuals shell too — a slim
            strip rather than a second header, since the shell already owns the breadcrumb and title. */}
        <div className="border-border flex items-center justify-end gap-1 border-b px-6 py-1.5">
          {backToDepartments ? (
            <Button variant="ghost" size="sm" onPress={backToDepartments} className="text-muted-foreground">
              {t("viewer.departments")}
            </Button>
          ) : null}
          {leave}
        </div>
        <ManualsViewShell
          facility={manualsNode}
          ancestors={kioskAncestors(manualsNode, byId, topIds)}
          session={session}
          // A kiosk reads. Every authoring gate is false, so the grid renders no Add / kebab / review controls at all.
          canManageManuals={false}
          canConfirmManuals={false}
          canManageFacility={false}
          // Not just "no permission" — the affordances are not rendered at all: no Add manual, no Favourites (the
          // account is shared), no Archived, no Area-info drawer.
          kiosk
          onHome={() => nav.drillTo(null)}
          onCrumb={(id) => nav.drillTo(id)}
          onBack={() => nav.closeManuals()}
        />
      </div>
    );
  }

  const drilled = nav.selectedId === null ? null : (treeById.get(nav.selectedId) ?? null);
  const inScope = drilled !== null && isInKioskScope(drilled, tops);
  const levelNodes: FacilityTreeNode[] = inScope
    ? drilled.children
    : tops.flatMap((t): FacilityTreeNode[] => {
        const node = treeById.get(t.id);
        return node ? [node] : [];
      });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-border border-b py-4">
        <div className="page-column flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="truncate text-xl font-semibold">{inScope ? drilled.name : department.name}</h1>
            {inScope ? (
              <FacilityBreadcrumb
                ancestors={kioskAncestors(drilled, byId, topIds)}
                current={drilled}
                onHome={() => nav.drillTo(null)}
                onCrumb={(id) => nav.drillTo(id)}
              />
            ) : (
              <p className="text-muted-foreground truncate text-sm">{landing.profile.name}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {backToDepartments ? (
              <Button variant="outline" size="sm" onPress={backToDepartments}>
                {t("viewer.departments")}
              </Button>
            ) : null}
            {leave}
          </div>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="page-column py-6">
          {facilities.isPending ? (
            <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("viewer.loadingFacilities")}
            </p>
          ) : levelNodes.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-16 text-center">
              <FacilityIcon aria-hidden="true" className="size-8 opacity-40" />
              <p className="text-sm">{t("viewer.nothingToOpenHere")}</p>
            </div>
          ) : (
            <FacilityRailList
              nodes={levelNodes}
              ariaLabel={inScope ? t("viewer.facilitiesIn", { name: drilled.name }) : t("viewer.facilitiesFor", { name: department.name })}
              emptyLine={t("viewer.nothingToOpenHere")}
              // No kebab, no star: a kiosk is a shared account, so "my favourites" would be everyone's, and there is
              // nothing here anyone is meant to manage.
              getKebab={() => null}
              showFavorite={false}
              favorites={EMPTY_FAVORITES}
              onToggleFavorite={noop}
              onDrill={(id) => nav.drillTo(id)}
              onOpenManuals={(id) => nav.openManuals(id)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const EMPTY_FAVORITES: ReadonlySet<number> = new Set<number>();
const noop = () => undefined;
